import React, { useCallback } from 'react';
import cn from 'classnames';
import { isString } from 'lodash';

import Button from 'components/form/Button.component';
import KnobInput from 'components/form/KnobInput.component';
import SMS_TEMPLATE from 'constants/salon/smsTemplate.const';
import useResettingState from 'hooks/useResettingState.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import asObject from 'utils/objects/asObject.util';
import isMissing from 'utils/predicates/any/isMissing.util';
import isEnumVal from 'utils/predicates/enum/isEnumVal.util';
import createNodeKey from 'utils/react/createNodeKey.util';

import CN from './WidgetSmsTemplates.module.scss';

// eslint-disable-next-line no-nested-ternary
const determineTemplate = (v) => (isString(v) ? SMS_TEMPLATE[v] || null : isEnumVal(SMS_TEMPLATE, v) ? v : null);

const WidgetSmsTemplates = ({
  name: nameFromProps,
  tag: tagFromProps,
  value: valueFromProps,
  disabled,
  options,
  onChange = alwaysNoop,
  onEdit = alwaysNoop,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const name = fromAnyToTrimmed(nameFromProps);
  const tag = fromAnyToTrimmed(tagFromProps);

  // NOTE: the resetting can be changed with the second parameter being useResettingState.NIL or useResettingState.NULL
  const [selected, setSelected] = useResettingState(determineTemplate(valueFromProps), useResettingState.NIL);
  const selectedId = asObject(selected).id;

  const handleClickKnob = useCallback(
    //
    (ev) => {
      const raw = selectedId === ev.name ? null : ev.raw;

      const { value } = asObject(call(`WidgetSmsTemplates.onChange`, onChange, { name, tag, raw }));

      const result = isMissing(value) ? raw : determineTemplate(value);
      setSelected(result);
    },
    [name, onChange, selectedId, setSelected, tag],
  );

  return (
    <div
      data-bem="WidgetSmsTemplates"
      data-tag={tag}
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <div data-bem="WidgetSmsTemplates__list" data-tag={tag} className={CN.list}>
        {asArray(options).map((option) => {
          const { id, imgSrc, descriptionTransKey } = option;

          const isSelected = selectedId === id;
          const handleClickEdit = () => {
            if (disabled) return;
            call(`WidgetSmsTemplates.onEdit`, onEdit, { name, tag, raw: id });
          };

          return (
            <div
              className={cn({
                [CN.templateContainer]: true,
                [CN.isSelected]: isSelected,
              })}
              key={createNodeKey({ tag: descriptionTransKey, unique: id })}
            >
              <KnobInput
                variant="imageBox"
                tag="WidgetSmsTemplates__item"
                name={id}
                value={option}
                selected={isSelected}
                disabled={disabled}
                onClick={handleClickKnob}
              />

              <div className={CN.imageContainer} style={{ backgroundImage: `url(${imgSrc})` }} />
              <div className={CN.flex}>
                <div className={CN.title}>{t(descriptionTransKey)}</div>
                {isSelected ? (
                  <Button variant="primary" disabled={disabled || !isSelected} onClick={handleClickEdit} width="small">
                    <div className={CN.button}>{t('reminders.editTextButton')}</div>
                  </Button>
                ) : (
                  <div className={CN.disabledText}>{t('reminders.editTextButton')}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
export default WidgetSmsTemplates;
